const content = `
# Component Forecasting Overview

Component forecasting predicts a building's electrical load by breaking it into four physical components and modeling each independently. Instead of treating load as a single opaque number, the system forecasts **HVAC**, **refrigeration**, **plug loads**, and **lighting** separately, then sums them for total load.

## Why Component-Based?

Traditional load forecasting treats the building as a black box — feed in weather and get a total kW. This fails at cold-start (no historical data) because there is nothing to learn from.

Component models solve cold-start by leveraging **NREL ComStock** — a dataset of 350,000 simulated commercial buildings with full end-use disaggregation. Each component model learns generalizable physical patterns (HVAC responds to temperature, lighting follows occupancy schedules) that transfer to unseen buildings.

## The Four Components

| Component | Driver | Typical Share |
|-----------|--------|--------------|
| **HVAC** | Temperature, humidity, building envelope | 30-50% of total load |
| **Refrigeration** | Ambient temperature, door openings | 10-30% (grocery/warehouse) |
| **Plug Loads** | Occupancy, business hours | 15-25% |
| **Lighting** | Occupancy, daylight, schedules | 10-20% |

## Pipeline Stages

The forecasting pipeline has five stages, each with a dedicated tab in the Forecasting section:

1. **Data Pipeline** — Download and process ComStock building profiles from NREL's S3 bucket. Extract metadata and hourly component profiles.
2. **Sampling** — Stratified sampling by building type, climate zone, and load characteristics to create a representative training set from 350k buildings.
3. **Training** — Train LightGBM models per component per building-type cluster with Optuna hyperparameter tuning. Save versioned model artifacts.
4. **Validation** — Run 5 forecast scenarios (perfect foresight through cold-start) and evaluate go/no-go criteria for production readiness.
5. **Cold-Start Prediction** — Given only building metadata (type, climate zone, sqft, vintage), produce an 8760-hour load forecast with per-component breakdown.

## Data Source: NREL ComStock

ComStock is the DOE/NREL commercial building stock model. It provides synthetic but physics-based hourly energy profiles for 350,000+ buildings across all US climate zones and building types. Data is publicly available on the OEDI S3 bucket with no credentials required.
`;

export default content;
